import {FormControl, FormField, Option, Separator} from 'platform/components';
import {Box, Grid, HStack, Show, Space, VStack} from 'platform/foundation';

import {FieldValues} from 'react-hook-form';

import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DepositFormValues} from '../../schemas/depositSchema';
import {DepositFormBinding} from '../../types/DepositFormBinding';
import {DocumentTemplateOptionGroup} from '../../types/DocumentTemplateOptionGroup';
import {getOrderPaymentTypeOptions} from '../../utils/getOrderPaymentTypeOptions';
import {DepositPaymentMethodCard} from './components/DepositPaymentMethodCard';

interface DepositSectionProps<TFieldValues extends FieldValues> extends RequiredTestIdProps {
  control: FormControl<TFieldValues>;
  binding: DepositFormBinding<TFieldValues>;
  deposit: DepositFormValues | Nullish;
  docSeriesOptions: Option[];
  isDocSeriesLoading?: boolean;
  /**
   * Grouped `workshop-invoice` template options. The owning page already resolves them through
   * `useDocumentTemplates`, so they are handed down instead of resolved again here - the section
   * and its three payment-method cards then share one resolution.
   */
  invoiceTemplateOptions: DocumentTemplateOptionGroup[];
  isTemplatesLoading?: boolean;
}

export function DepositSection<TFieldValues extends FieldValues>(
  props: DepositSectionProps<TFieldValues>
) {
  const orderPaymentTypeOptions = getOrderPaymentTypeOptions(props.deposit);

  return (
    <VStack spacing={4}>
      <FormField
        control={props.control}
        name={props.binding.allowDepositFieldName}
        type="switch"
        label={i18n.t('entity.settings.labels.allowDeposit')}
        data-testid={suffixTestId('allowDeposit', props)}
      />
      <Show when={props.deposit?.allowDeposit}>
        <Grid columns={2} horizontalSpacing={6}>
          <FormField
            control={props.control}
            name={props.binding.getFieldName('templateId')}
            type="choice"
            label={i18n.t('entity.settings.labels.depositTemplate')}
            options={props.invoiceTemplateOptions}
            isLoading={props.isTemplatesLoading}
            data-testid={suffixTestId('depositTemplate', props)}
          />
          <FormField
            control={props.control}
            name={props.binding.getFieldName('documentSeriesId')}
            type="choice"
            label={i18n.t('entity.settings.labels.depositDocumentSerie')}
            options={props.docSeriesOptions}
            isLoading={props.isDocSeriesLoading}
            data-testid={suffixTestId('depositDocumentSerie', props)}
          />
        </Grid>
        <Separator spacing={0} />
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              name={props.binding.getFieldName('defaultPaymentType')}
              type="choice"
              label={i18n.t('entity.order.labels.defaultPaymentMethod')}
              options={orderPaymentTypeOptions}
              isDisabled={!orderPaymentTypeOptions.length}
              data-testid={suffixTestId('depositDefaultPayment', props)}
            />
          </Box>
          <Space fillAvailable />
        </HStack>
        <DepositPaymentMethodCard
          control={props.control}
          binding={props.binding}
          method="cashPayment"
          title={i18n.t('entity.invoice.paymentMethod.cash')}
          isAllowed={props.deposit?.cashPayment?.isAllowed ?? false}
          defaultPaymentType={props.deposit?.defaultPaymentType}
          invoiceTemplateOptions={props.invoiceTemplateOptions}
          isTemplatesLoading={props.isTemplatesLoading}
          docSeriesOptions={props.docSeriesOptions}
          isDocSeriesLoading={props.isDocSeriesLoading}
          data-testid={suffixTestId('depositCashPayment', props)}
        />
        <DepositPaymentMethodCard
          control={props.control}
          binding={props.binding}
          method="cardPayment"
          title={i18n.t('entity.invoice.paymentMethod.card')}
          isAllowed={props.deposit?.cardPayment?.isAllowed ?? false}
          defaultPaymentType={props.deposit?.defaultPaymentType}
          invoiceTemplateOptions={props.invoiceTemplateOptions}
          isTemplatesLoading={props.isTemplatesLoading}
          docSeriesOptions={props.docSeriesOptions}
          isDocSeriesLoading={props.isDocSeriesLoading}
          data-testid={suffixTestId('depositCardPayment', props)}
        />
        <DepositPaymentMethodCard
          control={props.control}
          binding={props.binding}
          method="bankPayment"
          title={i18n.t('entity.invoice.paymentMethod.bankTransfer')}
          isAllowed={props.deposit?.bankPayment?.isAllowed ?? false}
          defaultPaymentType={props.deposit?.defaultPaymentType}
          invoiceTemplateOptions={props.invoiceTemplateOptions}
          isTemplatesLoading={props.isTemplatesLoading}
          docSeriesOptions={props.docSeriesOptions}
          isDocSeriesLoading={props.isDocSeriesLoading}
          data-testid={suffixTestId('depositBankPayment', props)}
        />
      </Show>
    </VStack>
  );
}
